import { Link } from "react-router";
import type { AccountsListResponse } from "../../lib/api-modules/types.js";
import { formatCentsCurrency } from "../../lib/util/money.js";
import {
  buildNetWorthBridge,
  formatIsoDate,
} from "../../lib/util/net-worth-bridge.js";
import { usePreferences } from "../../lib/preferences.js";

export function BalancesAsOf({ date }: { readonly date: string | null }) {
  if (date === null) return null;
  return (
    <p className="text-xs text-[var(--color-muted)]" data-testid="balances-as-of">
      Cash and margin as of {formatIsoDate(date)}.{" "}
      <Link to="/import" className="underline">
        Update from Merrill
      </Link>
    </p>
  );
}

function Step({
  sign,
  label,
  cents,
  hidden,
  strong = false,
}: {
  readonly sign?: string;
  readonly label: string;
  readonly cents: number;
  readonly hidden: boolean;
  readonly strong?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      {sign && (
        <span aria-hidden className="text-[var(--color-muted)]">
          {sign}
        </span>
      )}
      <div>
        <dt className="text-xs text-[var(--color-muted)]">{label}</dt>
        <dd
          className={`font-mono tabular-nums ${strong ? "font-medium" : ""}`}
          data-testid={`bridge-${label.toLowerCase().replaceAll(" ", "-")}`}
        >
          {hidden ? "••••" : formatCentsCurrency(Math.abs(cents))}
        </dd>
      </div>
    </div>
  );
}

/** How investments, cash and debts add up to net worth. */
export function NetWorthBridge({
  totals,
}: {
  readonly totals: AccountsListResponse["totals"];
}) {
  const { privacyMode } = usePreferences();
  const bridge = buildNetWorthBridge({
    investments_cents: totals.positions_value_cents,
    cash_cents: totals.cash_balance_cents,
    margin_cents: totals.margin_balance_cents,
    net_worth_cents: totals.total_value_cents,
  });
  return (
    <section aria-label="How this adds up to net worth" className="space-y-2">
      <dl
        className="flex flex-wrap items-baseline gap-x-5 gap-y-2 text-sm"
        data-testid="net-worth-bridge"
      >
        <Step label="Investments" cents={bridge.investments_cents} hidden={privacyMode} />
        <Step sign="+" label="Cash" cents={bridge.cash_cents} hidden={privacyMode} />
        {bridge.margin_cents !== 0 && (
          <Step sign="−" label="Margin loan" cents={bridge.margin_cents} hidden={privacyMode} />
        )}
        {bridge.other_cents !== 0 && (
          <Step
            sign={bridge.other_cents < 0 ? "−" : "+"}
            label={bridge.other_cents < 0 ? "Other debts" : "Other"}
            cents={bridge.other_cents}
            hidden={privacyMode}
          />
        )}
        <Step
          sign="="
          label="Net worth"
          cents={bridge.net_worth_cents}
          hidden={privacyMode}
          strong
        />
      </dl>
      <BalancesAsOf date={totals.balances_as_of_date} />
    </section>
  );
}
